import moment from 'moment-timezone';

const DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_TIME_ZONE = 'Etc/GMT-3';

export const getBrowserTimeZone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();

export const getTimetableTimeZones = (platformTimeZone, browserTimeZone = getBrowserTimeZone()) => {
    const platform = moment.tz.zone(platformTimeZone || '') ? platformTimeZone : DEFAULT_TIME_ZONE;
    return {
        platformTimeZone: platform,
        timeZone: moment.tz.zone(browserTimeZone || '') ? browserTimeZone : platform,
    };
};

export const getTimeZoneLabel = (timeZone, date = new Date()) => {
    const offset = moment(date).tz(timeZone).utcOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    return `GMT${offset >= 0 ? '+' : '-'}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
};

export const getTimetableDates = (start, end) => {
    const dates = [];
    const current = moment.utc(start, DATE_FORMAT, true);
    const last = moment.utc(end, DATE_FORMAT, true);
    while (current.isValid() && last.isValid() && !current.isAfter(last, 'day')) {
        dates.push(current.format(DATE_FORMAT));
        current.add(1, 'day');
    }
    return dates;
};

export const getTimetableRequestRange = (start, end, platformTimeZone, timeZone) => ({
    // Include lessons that started on the preceding platform day and run overnight.
    start: moment.tz(start, DATE_FORMAT, timeZone).startOf('day')
        .tz(platformTimeZone).subtract(1, 'day').format(DATE_FORMAT),
    end: moment.tz(end, DATE_FORMAT, timeZone).endOf('day')
        .tz(platformTimeZone).format(DATE_FORMAT),
});

// The original report is used for editing, registration and platform-time checks.
export const getTimetableSourceReport = (report) => report?.platformReport ?? report;

export const convertTimetableReport = (report, platformTimeZone, timeZone) => {
    const source = getTimetableSourceReport(report);
    if (!source?.date_conference || !source?.start_time || platformTimeZone === timeZone) return source;

    const formats = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'];
    const start = moment.tz(`${source.date_conference} ${source.start_time}`, formats, true, platformTimeZone);
    const end = moment.tz(`${source.date_conference} ${source.end_time}`, formats, true, platformTimeZone);
    if (!start.isValid() || !end.isValid()) return source;
    if (end.isBefore(start)) end.add(1, 'day');
    start.tz(timeZone);
    end.tz(timeZone);
    const openedAt = source.opened_at && moment.tz(source.opened_at, platformTimeZone);

    return {
        ...source,
        date_conference: start.format(DATE_FORMAT),
        start_time: start.format('HH:mm'),
        end_time: end.format('HH:mm'),
        ...(openedAt?.isValid() ? { opened_at: openedAt.tz(timeZone).format('YYYY-MM-DD HH:mm:ss') } : {}),
        displayEndDate: end.format(DATE_FORMAT),
        platformReport: source,
    };
};

export const getTimetableReportsForDay = (reports, date) => reports.flatMap((report) => {
    if (report.date_conference === date) return [report];
    const endDate = report.displayEndDate;
    if (report.date_conference < date && endDate >= date && !(endDate === date && report.end_time === '00:00')) {
        return [{
            ...report,
            date_conference: date,
            start_time: '00:00:00',
            continuation_date: report.date_conference,
            continuation_start_time: report.start_time,
            isOvernightContinuation: true,
        }];
    }
    return [];
});

export const convertTimetableData = (groups, { start, end, platformTimeZone, timeZone, isOneDay = false }) => {
    if (isOneDay) {
        return groups.map((group) => ({
            ...group,
            reports: getTimetableReportsForDay(
                (group.reports ?? []).map((report) => convertTimetableReport(report, platformTimeZone, timeZone)),
                start,
            ),
        }));
    }

    const previousDate = moment.utc(start).subtract(1, 'day').format(DATE_FORMAT);
    const days = getTimetableDates(previousDate, end).map((date) => ({ date, reports: [] }));
    const daysByDate = new Map(days.map((day) => [day.date, day]));
    groups.forEach((group) => {
        (group.reports ?? []).forEach((report) => {
            const converted = convertTimetableReport(report, platformTimeZone, timeZone);
            const targetDay = daysByDate.get(converted.date_conference);
            if (targetDay) targetDay.reports.push(converted);
        });
    });
    return days;
};

export const mergeDailyTimetableData = (responses) => {
    // The daily API uses the filter order for its person columns (as does Main).
    const columns = [];
    responses.forEach(({ data }) => {
        (data.data ?? []).forEach((column, index) => {
            if (!columns[index]) columns[index] = { ...column, reports: [] };
            const ids = new Set(columns[index].reports.map((report) => report.id));
            columns[index].reports.push(...(column.reports ?? []).filter((report) => !ids.has(report.id)));
        });
    });
    return columns;
};

const clipIntervalToDay = (interval, date) => {
    const startDate = interval.date_conference;
    const endDate = interval.displayEndDate;
    if (startDate > date || endDate < date || (endDate === date && interval.end_time === '00:00')) return null;
    return {
        ...interval,
        start_time: startDate < date ? '00:00' : interval.start_time,
        end_time: endDate > date ? '24:00' : interval.end_time,
    };
};

export const getTimetableWorkingHoursForDay = (workingHours = [], date, platformTimeZone, timeZone) => {
    if (!workingHours?.length) return [];
    if (platformTimeZone === timeZone) {
        const weekDay = moment.utc(date).isoWeekday() - 1;
        return workingHours.filter((item) => Number(item.week_day) === weekDay);
    }
    const range = getTimetableRequestRange(date, date, platformTimeZone, timeZone);
    return getTimetableDates(range.start, range.end).flatMap((platformDate) => (
        workingHours
            .filter((item) => Number(item.week_day) === moment.utc(platformDate).isoWeekday() - 1)
            .map((item) => convertTimetableReport({ ...item, date_conference: platformDate }, platformTimeZone, timeZone))
            .map((item) => clipIntervalToDay(item, date))
            .filter(Boolean)
    ));
};

export const convertTimetableFreeHours = (data, start, end, platformTimeZone, timeZone) => {
    if (platformTimeZone === timeZone) return data;
    return Object.fromEntries(Object.entries(data ?? {}).map(([id, employee]) => {
        const intervals = { free: [], busy: [] };
        (employee.days ?? []).forEach((day) => {
            Object.keys(intervals).forEach((key) => {
                intervals[key].push(...(day[key] ?? []).map((interval) => convertTimetableReport(
                    { ...interval, date_conference: day.date }, platformTimeZone, timeZone,
                )));
            });
        });
        return [id, {
            ...employee,
            days: getTimetableDates(start, end).map((date) => ({
                date,
                ...Object.fromEntries(Object.entries(intervals).map(([key, values]) => [key, values
                    .map((interval) => clipIntervalToDay(interval, date))
                    .filter(Boolean)
                    .sort((first, second) => first.start_time.localeCompare(second.start_time))])),
            })),
        }];
    }));
};
